import { CdkTrapFocus } from '@angular/cdk/a11y';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  INJECTOR,
  linkedSignal,
  Pipe,
  PipeTransform,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { handleRequestError } from '@softline/application';
import { AuthenticationContextService } from '@softline/auth';
import { HttpDataAccess, isDefinedNotEmpty, uuid } from '@softline/core';
import { ObserveElementSizeDirective, UiCoreModule } from '@softline/ui-core';
import {
  animationFrameScheduler,
  concat,
  concatMap,
  debounceTime,
  defer,
  delay,
  distinctUntilChanged,
  finalize,
  from,
  ignoreElements,
  interval,
  map,
  Observable,
  of,
  repeat,
  Subject,
  subscribeOn,
  take,
  throttleTime,
} from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LottieAnimationPlayerComponent } from '../lottie-animation-player/lottie-animation-player.component';
import { MarkdownComponent, MARKED_OPTIONS, provideMarkdown } from 'ngx-markdown';

declare const CHATBOT_API: any;

const typed = ({ word, speed, backwards = false }) =>
  interval(speed).pipe(
    map((x) => (backwards ? word.substr(0, word.length - x - 1) : word.substr(0, x + 1))),
    take(word.length)
  );

@Pipe({
  name: 'typewriterEffect',
})
export class TypewriterEffectPipe implements PipeTransform {
  transform(value: string | undefined, cb?: () => void): Observable<string> {
    return defer(() => {
      return from([value])
        .pipe(
          concatMap((text) => concat(typed({ word: text, speed: 10 }))),
          subscribeOn(animationFrameScheduler)
        )
        .pipe(finalize(() => cb?.()));
    });
  }
}

@Component({
  selector: 'soft-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.css'],
  imports: [
    RouterModule,
    UiCoreModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    CdkTrapFocus,
    AsyncPipe,
    TypewriterEffectPipe,
    ObserveElementSizeDirective,
    FormsModule,
    LottieAnimationPlayerComponent,
    MarkdownComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          gfm: true,
          breaks: true,
          pedantic: false,
        },
      },
    }),
  ],
})
export class ChatbotComponent {
  readonly #service = inject(AuthenticationContextService);
  readonly #dataAccess = inject(HttpDataAccess);
  readonly #router = inject(Router);
  readonly #injector = inject(INJECTOR);

  readonly positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.metaKey && (event.key === 'k' || event.key === 'K')) {
      this.openCloseOverlay();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  readonly overlayOpen = signal(false);

  readonly messageInput = linkedSignal<boolean, string | undefined>({
    source: this.overlayOpen,
    computation: () => undefined,
  });

  readonly endElement = viewChild<ElementRef<HTMLDivElement>>('end');
  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('input');

  readonly requestInProgress = signal(false);

  readonly _hoverIndex = signal(-1);

  readonly hoverIndex = toSignal(
    toObservable(this._hoverIndex).pipe(distinctUntilChanged(), debounceTime(120))
  );

  readonly elements = viewChildren<ElementRef<HTMLLIElement>>('listElement');

  readonly chatId = linkedSignal(() => `sl_chat_${uuid()}`);

  readonly _messages = signal<
    { response: boolean; text: string; link?: string; buttons?: string[] }[]
  >([]);

  readonly currentMessages = linkedSignal<
    boolean,
    { response: boolean; text: string; link?: string; buttons?: string[] }[]
  >({
    source: this.overlayOpen,
    computation: () => {
      return this._messages().length > 0
        ? []
        : [
            {
              text: 'Hallo! Wie kann ich Ihnen behilflich sein?',
              response: true,
            },
          ];
    },
  });

  readonly messages = computed(() => {
    return this._messages().concat(...this.currentMessages());
  });

  readonly sizeChange$ = new Subject<DOMRect>();

  readonly typewriterLoadingTextEffect$ = defer(() =>
    from(['Bitte warten...']).pipe(
      concatMap((text) =>
        concat(
          typed({ word: text, speed: 50 }),
          of('').pipe(delay(1000), ignoreElements()), // pause
          typed({ word: text, speed: 30, backwards: true }), // delete
          of('').pipe(delay(200), ignoreElements()) // pause
        )
      ),
      repeat(10)
    )
  );

  constructor() {
    this.sizeChange$
      .pipe(
        map((o) => o?.height),
        distinctUntilChanged(),
        throttleTime(100),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.scrollToEndElement();
      });

    afterRenderEffect(() => {
      const isOpen = this.overlayOpen();
      const elements = this.elements();
      const endElement = this.endElement();

      if (isOpen && elements.length > 0 && !!endElement) {
        setTimeout(() => {
          this.scrollToEndElement();
        }, 100);
      }
    });
  }

  animationPlayingStarted() {
    this.scrollToEndElement();
  }

  openCloseOverlay(): void {
    if (this.overlayOpen()) {
      this.closeOverlay();
    } else {
      this.openOverlay();
    }
  }

  openOverlay(): void {
    this.overlayOpen.set(true);
  }

  closeOverlay() {
    this._messages.update((messages) => [...messages, ...this.currentMessages()]);
    this.overlayOpen.set(false);
  }

  onOverlayKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeOverlay();
    }
  }

  async onSubmit(message: string | undefined): Promise<void> {
    if (!message || this.requestInProgress()) return;

    try {
      this.currentMessages.update((messages) => [
        ...messages,
        { response: false, text: message },
      ]);

      this.messageInput.set('');
      this.requestInProgress.set(true);

      /*
      const response = await this.#dataAccess.create<
        { message: string; chatId: string },
        { text: string }
      >(
        {
          url: 'https://n8n.rht.tirol/webhook/d68be8bd-0633-4203-98fb-1ff69fb7bae9',
        },
        {
          chatId: this.chatId(),
          message,
        }
      ); */

      const res = await fetch(CHATBOT_API, {
        method: 'POST',
        body: JSON.stringify({
          chatId: this.chatId(),
          message,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      const response = await res.json();

      this.currentMessages.update((messages) => [
        ...messages,
        {
          response: true,
          text:
            response?.text?.replace?.(/(?<!\|)\n(?!\|)/g, '<br />') ??
            'Darauf weiß ich leider keine Antwort...',
          link: response.link,
          buttons: response.buttons,
        },
      ]);
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    } finally {
      this.requestInProgress.set(false);
    }
  }

  copyTextToInput(text?: string): void {
    this.messageInput.set(text?.replace?.(/<br \/>/g, '\n '));
    this.inputElement()?.nativeElement?.focus();
  }

  scrollToEndElement = () => {
    this.endElement()?.nativeElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  };

  public readonly navigateToLink = (url: string) => {
    const url2 = `${location.protocol}//${window.location.host}`;
    url = url.replace(url2, '');
    window.open(url, '_blank');
  };

  protected readonly open = open;
  protected readonly isDefinedNotEmpty = isDefinedNotEmpty;
}
